import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeftIcon as ArrowLeft,
  DatabaseIcon as Database,
  StackIcon as Stack,
  CodeIcon as Code,
  FilesIcon as Files,
  ArchiveIcon as Archive,
  TrendUpIcon as TrendUp,
  TargetIcon as Target,
  WarningIcon as Warning,
  GraphIcon as Graph,
  CircleNotchIcon as Spinner,
  ArrowRightIcon as ArrowRight,
  ShieldCheckIcon as ShieldCheck,
  FilePdfIcon as FilePdf,
  EnvelopeSimpleIcon as Envelope,
  ClockClockwiseIcon as ClockClockwise,
  SparkleIcon as Sparkle,
  ShareNetworkIcon as ShareNetwork,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getMetadataDashboard, getWorkspace, getSteeringBundlePdfUrl } from "@/lib/api";
import JournalChurnPanel from "@/components/metadata/JournalChurnPanel";
import DecommissionRadarPanel from "@/components/metadata/DecommissionRadarPanel";
import SendBundleModal from "@/components/workspace/SendBundleModal";
import BundleScheduleModal from "@/components/workspace/BundleScheduleModal";
import ShareBundleModal from "@/components/workspace/ShareBundleModal";
import BundleSnapshotsList from "@/components/workspace/BundleSnapshotsList";

export default function WorkspaceMetadataDashboard() {
  const { id } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailBundleOpen, setEmailBundleOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [snapshotsRefresh, setSnapshotsRefresh] = useState(0);

  useEffect(() => {
    getWorkspace(id).then((d) => setWorkspace(d?.workspace || null));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    getMetadataDashboard(id)
      .then(setData)
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load dashboard.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const counts = data?.counts || {};
  const totalFiles = (counts.physical_files || 0) + (counts.logical_files || 0);

  return (
    <div className="space-y-6" data-testid="metadata-dashboard-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/workspaces/${id}/metadata`}
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1"
            data-testid="back-to-metadata"
          >
            <ArrowLeft size={11} weight="bold" />
            Back to repository
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <Graph size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Modernization dashboard
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
            Operational rollup of this workspace&apos;s IBM i catalog —
            coverage counts, journaling posture, biggest data
            assets, most-coupled programs, and risk candidates.
          </p>
        </div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
          Workspace<br />
          <span className="text-[var(--ml-text-primary)]">{workspace?.name || "—"}</span>
        </div>
      </div>

      {!loading && data && (
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="dashboard-actions">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/workspaces/${id}/metadata/opportunities`}
              data-testid="open-opportunities-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--ml-primary)] text-white text-sm font-medium hover:bg-[var(--ml-primary-hover)] transition-colors"
            >
              <Sparkle size={14} weight="bold" />
              Modernization opportunities (AI)
            </Link>
            <a
              href={getSteeringBundlePdfUrl(id)}
              data-testid="bundle-pdf-btn"
              title="Download Risk Brief + Top Write Churn + Decommission Radar as one PDF"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--ml-border)] text-sm font-medium hover:bg-[var(--ml-surface)] transition-colors"
            >
              <FilePdf size={14} weight="bold" />
              Bundle as PDF
            </a>
            <button
              type="button"
              onClick={() => setEmailBundleOpen(true)}
              data-testid="bundle-email-btn"
              title="Email the bundle PDF + executive read-out to a teammate or external stakeholder"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--ml-border)] text-sm font-medium hover:bg-[var(--ml-surface)] transition-colors"
            >
              <Envelope size={14} weight="bold" />
              Email bundle
            </button>
            <button
              type="button"
              onClick={() => setScheduleOpen(true)}
              data-testid="bundle-schedule-btn"
              title="Configure weekly auto-send of the bundle to a distribution list"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--ml-border)] text-sm font-medium hover:bg-[var(--ml-surface)] transition-colors"
            >
              <ClockClockwise size={14} weight="bold" />
              Schedule weekly
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              data-testid="bundle-share-btn"
              title="Mint a public read-only snapshot link for execs who don't have a Modernization Lab login"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--ml-border)] text-sm font-medium hover:bg-[var(--ml-surface)] transition-colors"
            >
              <ShareNetwork size={14} weight="bold" />
              Share snapshot
            </button>
          </div>
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Claude Sonnet 4.5 · risk · orphan · journaling · microservice
          </span>
        </div>
      )}

      {loading ? (
        <div className="ml-card p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]">
          <Spinner size={18} className="animate-spin mr-2" /> Crunching the catalog…
        </div>
      ) : !data ? (
        <div className="ml-card p-12 text-center">
          <Warning size={36} weight="duotone" color="#da1e28" className="mx-auto mb-3" />
          <div className="font-medium mb-1">Dashboard unavailable</div>
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
            We couldn&apos;t load the rollup. Try refreshing, or import some
            metadata first via the repository page.
          </p>
        </div>
      ) : (
        <>
          {/* Top-row counter widgets */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-counters">
            <CounterWidget
              label="Libraries"
              value={counts.libraries || 0}
              icon={Database}
              testid="widget-libraries"
            />
            <CounterWidget
              label="Objects"
              value={counts.objects || 0}
              icon={Stack}
              testid="widget-objects"
            />
            <CounterWidget
              label="Programs"
              value={counts.programs || 0}
              icon={Code}
              testid="widget-programs"
            />
            <CounterWidget
              label="Files (PF + LF)"
              value={totalFiles}
              sub={`${counts.physical_files || 0} PF · ${counts.logical_files || 0} LF`}
              icon={Files}
              testid="widget-files"
            />
          </div>

          {/* Second row — Journaling + Unused */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <JournalWidget counts={counts} />
            <UnusedFilesWidget
              count={data.unused_files_count || 0}
              sample={data.unused_files || []}
              total={counts.physical_files || 0}
            />
            <DependencyStatsWidget counts={counts} />
          </div>

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TopReferencedFilesWidget rows={data.top_referenced_files || []} workspaceId={id} />
            <LargestFilesWidget rows={data.largest_files || []} />
          </div>

          <CoupledProgramsWidget rows={data.most_coupled_programs || []} workspaceId={id} />

          <JournalChurnPanel workspaceId={id} />

          <DecommissionRadarPanel workspaceId={id} />

          <BundleSnapshotsList
            workspaceId={id}
            canWrite
            refreshKey={snapshotsRefresh}
          />
        </>
      )}

      <SendBundleModal
        workspaceId={id}
        workspaceName={workspace?.name}
        clientName={workspace?.client_name}
        open={emailBundleOpen}
        onClose={() => setEmailBundleOpen(false)}
      />

      <BundleScheduleModal
        workspaceId={id}
        workspaceName={workspace?.name}
        clientName={workspace?.client_name}
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
      />

      <ShareBundleModal
        workspaceId={id}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onCreated={() => setSnapshotsRefresh((n) => n + 1)}
      />
    </div>
  );
}

function CounterWidget({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      className="ml-card p-4 border-l-2 border-[var(--ml-primary)]"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {label}
        </div>
        <Icon size={16} weight="duotone" color="#0f62fe" />
      </div>
      <div className="text-3xl font-semibold tabular-nums mt-2" data-testid={`${testid}-value`}>
        {value.toLocaleString()}
      </div>
      {sub && (
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-1">
          {sub}
        </div>
      )}
    </div>
  );
}

function JournalWidget({ counts }) {
  const total = counts.physical_files || 0;
  const journaled = counts.journaled_files || 0;
  const pct = counts.journaled_pct || 0;
  const unjournaled = Math.max(0, total - journaled);

  // Color band: red < 50%, amber 50-79%, green ≥ 80%.
  const band =
    pct >= 80
      ? { fg: "#0e6027", bg: "#defbe6", label: "Healthy" }
      : pct >= 50
      ? { fg: "#a56600", bg: "#fcf4d6", label: "At risk" }
      : { fg: "#a2191f", bg: "#fff1f1", label: "Critical" };

  return (
    <div className="ml-card p-4" data-testid="widget-journal">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Journaled files
          </div>
          <div className="text-3xl font-semibold tabular-nums mt-2" data-testid="widget-journal-pct">
            {pct.toFixed(1)}%
          </div>
        </div>
        <ShieldCheck size={20} weight="duotone" color={band.fg} />
      </div>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-1">
        {journaled.toLocaleString()} of {total.toLocaleString()} PFs
      </div>
      <div className="h-2 bg-[var(--ml-surface)] mt-3 overflow-hidden">
        <div
          className="h-full transition-[width]"
          style={{ width: `${Math.min(100, pct)}%`, background: band.fg }}
          data-testid="widget-journal-bar"
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span
          className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5"
          style={{ color: band.fg, background: band.bg }}
          data-testid="widget-journal-band"
        >
          {band.label}
        </span>
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {unjournaled.toLocaleString()} unjournaled
        </span>
      </div>
    </div>
  );
}

function UnusedFilesWidget({ count, sample, total }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="ml-card p-4" data-testid="widget-unused-files">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Unused physical files
          </div>
          <div
            className="text-3xl font-semibold tabular-nums mt-2"
            data-testid="widget-unused-count"
          >
            {count.toLocaleString()}
          </div>
        </div>
        <Archive size={20} weight="duotone" color="#525252" />
      </div>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-1">
        {pct.toFixed(1)}% of catalog · candidates for archival
      </div>
      {sample.length > 0 && (
        <div className="mt-3 max-h-32 overflow-y-auto text-xs space-y-1" data-testid="widget-unused-sample">
          {sample.slice(0, 6).map((row) => (
            <div
              key={`${row.library_name}/${row.file_name}`}
              className="flex justify-between gap-2 border-b border-[var(--ml-border)] last:border-b-0 pb-1 last:pb-0"
            >
              <span className="font-mono truncate">
                <span className="text-[var(--ml-text-secondary)]">{row.library_name}/</span>
                {row.file_name}
              </span>
              <span className="text-[var(--ml-text-secondary)] tabular-nums shrink-0">
                {row.record_count != null ? `${row.record_count.toLocaleString()} rows` : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DependencyStatsWidget({ counts }) {
  const refs = counts.program_references || 0;
  const rels = counts.file_relationships || 0;
  const fields = counts.fields || 0;
  return (
    <div className="ml-card p-4" data-testid="widget-dependencies">
      <div className="flex items-start justify-between gap-2">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Coupling graph
        </div>
        <Graph size={20} weight="duotone" color="#0f62fe" />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <StatBlock label="Pgm refs"    value={refs} testid="dep-refs" />
        <StatBlock label="File rels"   value={rels} testid="dep-rels" />
        <StatBlock label="Fields"      value={fields} testid="dep-fields" />
      </div>
      <p className="text-xs text-[var(--ml-text-secondary)] mt-3">
        Total edges in the dependency graph across this workspace&apos;s catalog.
      </p>
    </div>
  );
}

function StatBlock({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xl font-semibold tabular-nums">
        {(value || 0).toLocaleString()}
      </div>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        {label}
      </div>
    </div>
  );
}

function TopReferencedFilesWidget({ rows, workspaceId }) {
  return (
    <div className="ml-card p-4" data-testid="widget-top-referenced">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Most-touched files
          </div>
          <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
            <Target size={16} weight="duotone" color="#0f62fe" />
            Top referenced
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyStrip text="Import program references (DSPPGMREF) to see the most-touched files." />
      ) : (
        <table className="w-full text-sm" data-testid="widget-top-referenced-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Pgms</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Refs</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Jrn</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Trace</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.library_name}/${r.file_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
                <td className="px-2 py-1.5 font-mono text-xs">
                  <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                  {r.file_name}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{(r.program_ref_count || 0).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{(r.dependent_count || 0).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right">
                  {r.journaled ? (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[#0e6027]">yes</span>
                  ) : (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[#a2191f]">no</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Link
                    to={`/workspaces/${workspaceId}/metadata/impact?object=${encodeURIComponent(r.file_name)}&library=${encodeURIComponent(r.library_name)}&depth=2`}
                    data-testid={`trace-file-${r.library_name}-${r.file_name}`}
                    className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline"
                  >
                    →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LargestFilesWidget({ rows }) {
  return (
    <div className="ml-card p-4" data-testid="widget-largest-files">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            By record count
          </div>
          <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
            <TrendUp size={16} weight="duotone" color="#0f62fe" />
            Largest files
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyStrip text="Import physical files (DSPFD) to see the largest data assets." />
      ) : (
        <table className="w-full text-sm" data-testid="widget-largest-files-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Records</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Size</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Jrn</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.library_name}/${r.file_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
                <td className="px-2 py-1.5 font-mono text-xs">
                  <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                  {r.file_name}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {r.record_count != null ? r.record_count.toLocaleString() : "—"}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {r.file_size != null ? formatBytes(r.file_size) : "—"}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {r.journaled ? (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[#0e6027]">yes</span>
                  ) : (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[#a2191f]">no</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CoupledProgramsWidget({ rows, workspaceId }) {
  return (
    <div className="ml-card p-4" data-testid="widget-coupled-programs">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            By total references
          </div>
          <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
            <Code size={16} weight="duotone" color="#0f62fe" />
            Most-coupled programs
          </div>
        </div>
        <Link
          to={`/workspaces/${workspaceId}/metadata/impact`}
          data-testid="open-impact-from-dashboard"
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline hidden sm:flex items-center gap-1"
        >
          Impact analysis
          <ArrowRight size={11} weight="bold" />
        </Link>
      </div>
      {rows.length === 0 ? (
        <EmptyStrip text="Import programs and program references to see coupling." />
      ) : (
        <table className="w-full text-sm" data-testid="widget-coupled-programs-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Program</th>
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Type</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File refs</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Pgm refs</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Total</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Trace</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.library_name}/${r.program_name}`} className="border-b border-[var(--ml-border)] last:border-b-0">
                <td className="px-2 py-1.5 font-mono text-xs">
                  <span className="text-[var(--ml-text-secondary)]">{r.library_name}/</span>
                  {r.program_name}
                </td>
                <td className="px-2 py-1.5">
                  <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                    {r.object_type || "—"}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{(r.file_refs || 0).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{(r.pgm_refs || 0).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{(r.total_refs || 0).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right">
                  <Link
                    to={`/workspaces/${workspaceId}/metadata/impact?object=${encodeURIComponent(r.program_name)}&library=${encodeURIComponent(r.library_name)}&depth=2`}
                    data-testid={`trace-program-${r.library_name}-${r.program_name}`}
                    className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline"
                  >
                    →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EmptyStrip({ text }) {
  return (
    <div className="py-6 text-center text-sm text-[var(--ml-text-secondary)] border border-dashed border-[var(--ml-border)]">
      {text}
    </div>
  );
}

function formatBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
